import cx from 'clsx';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import { ReactComponent as EyeIcon } from '@assets/svg/ic-eye.svg';
import { ReactComponent as EyeOffIcon } from '@assets/svg/ic-eyeoff.svg';
import { ReactComponent as TypeNewPasswordDarkIcon } from '@assets/svg/ic-newPassword-dark.svg';
import { ReactComponent as TypeNewPasswordIcon } from '@assets/svg/ic-newPassword.svg';

import Button from '@components/uikit/Button/Button';
import Input from '@components/uikit/Input/Input';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useUserChangePasswordMutation } from '@services/user/useUserChangePasswordMutation';

import { selectAppRtl } from '@store/useAppStore';
import { selectAppPersistDarkMode } from '@store/useAppStorePersist';
import { useModalActions } from '@store/useModalStore';

import ModalHeader from '../ModalHeader/ModalHeader';
import {
  CHANGE_PASSWORD_FIELDS,
  changePasswordDefaultValue,
  schemaChangePassword,
} from './constants/schemaChangePassword';
import style from './style.module.scss';

const ChangePasswordModal = () => {
  const { t } = useTranslation();
  const rtl = selectAppRtl();

  const { setModalClose } = useModalActions();

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    control,
    reset,
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = useForm({
    mode: 'all',
    resolver: schemaChangePassword,
    defaultValues: changePasswordDefaultValue,
  });

  const { mutate: userChangePasswordMutate } = useUserChangePasswordMutation();

  const submit = handleSubmit(
    (data) =>
      userChangePasswordMutate(data, {
        onSuccess: () => {
          showToast({ variant: 'success', message: t('Your password has been successfully changed.') });
          closeModal();
        },
        onError: (error) => {
          const [name, message] = Object.entries(error.response.data.errors)[0];
          setError(name, { message: message[0] });
        },
      }),
    (errors) => console.error(errors),
  );

  const closeModal = () => {
    setModalClose();
    reset();
  };

  const getIcon = (state, setState) => {
    const onClick = () => setState(!state);
    return !state ? (
      <EyeOffIcon onClick={onClick} className={style.eyeOffIcon} />
    ) : (
      <EyeIcon onClick={onClick} className={style.eyeIcon} />
    );
  };

  const darkMode = selectAppPersistDarkMode();

  return (
    <div className={cx(style.modalChangePassword, { [style.bodyRtl]: rtl })}>
      <ModalHeader title={t('Set new password')} />
      <div className={style.modalBody}>
        <div className={style.cardBlock}>
          <div className={style.cardItem}>
            <div className={style.cardItemIconWrapper}>
              {darkMode ? (
                <TypeNewPasswordDarkIcon className={style.cardItemIconDark} />
              ) : (
                <TypeNewPasswordIcon />
              )}
            </div>
            <p className={style.cardItemTitle}>{t('Set new password')}</p>
            <p className={style.cardItemDescription}>
              {t('Password must be at least 8 characters with at least 1 number, 1 special character')}
            </p>
          </div>
        </div>
        <div className={style.profileChangePasswordForm}>
          <Input
            label={t('Current password')}
            required
            control={control}
            name={CHANGE_PASSWORD_FIELDS.OLD_PASSWORD}
            inputType={showOldPassword ? 'text' : 'password'}
            rightElement={getIcon(showOldPassword, setShowOldPassword)}
          />
          <Input
            label={t('Enter New Password')}
            required
            name={CHANGE_PASSWORD_FIELDS.PASSWORD}
            control={control}
            inputType={showPassword ? 'text' : 'password'}
            rightElement={getIcon(showPassword, setShowPassword)}
          />
          <Input
            label={t('Confirm Your Password')}
            required
            name={CHANGE_PASSWORD_FIELDS.CONFIRM_PASSWORD}
            control={control}
            inputType={showConfirmPassword ? 'text' : 'password'}
            rightElement={getIcon(showConfirmPassword, setShowConfirmPassword)}
          />
        </div>
      </div>
      <div className={style.modalFooter}>
        <Button type="secondary" text={t('Cancel')} onClick={closeModal} />
        <Button type="primary" text={t('Change Password')} disabled={isSubmitting} onClick={submit} />
      </div>
    </div>
  );
};

export default ChangePasswordModal;
